import { useCallback, useMemo } from 'react';
import axios, { type AxiosStatic, type AxiosRequestConfig } from 'axios';
import { getOdataRootUrl } from '@bingads-webui-react/copilot-utils';
import { useAdsCopilotContext } from '@bingads-webui/ads-copilot-ui';

export type CopilotOData = {
  get: AxiosStatic['get'];
  post: AxiosStatic['post'];
};

/**
 * Provides a way to fetch from odata whether Copilot is being hosted in Campaign UI or ClientCenter UI.
 */
export function useOData(skipAccountId?: boolean) {
  const { botServiceConfig } = useAdsCopilotContext() || {};

  const getODataUrl = useCallback((customerId: string, accountId: string, urlSuffix: string) => {
    const rootUrl = getOdataRootUrl({ accountId: skipAccountId ? undefined : accountId, customerId });
    return `${rootUrl}${urlSuffix}`;
  }, [skipAccountId]);

  const ret = useMemo<CopilotOData | undefined>(() => {
    if (botServiceConfig) {
      const {
        accountId,
        ccSmallToken,
        customerId,
      } = botServiceConfig;

      return {
        get: (urlSuffix: string, config?: AxiosRequestConfig) => {
          const url = getODataUrl(customerId, accountId, urlSuffix);

          return axios.get(url, {
            ...config,
            headers: {
              Authorization: ccSmallToken,
              'Content-Type': 'application/json',
              ...config?.headers,
            },
          });
        },
        post: (urlSuffix: string, data: any, config?: AxiosRequestConfig) => {
          const url = getODataUrl(customerId, accountId, urlSuffix);

          return axios.post(url, data, {
            ...config,
            headers: {
              Authorization: ccSmallToken,
              'Content-Type': 'application/json',
              ...config?.headers,
            },
          });
        },
      };
    }

    return undefined;
  }, [botServiceConfig, getODataUrl]);

  return ret;
}
